import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { createPortal } from 'react-dom';
import { NodeType, ImageGenerationMode, AppNode } from '@/types';
import { Palette, MapPin, Focus, VideoIcon, Users } from 'lucide-react';
import { useNodeStore } from '@/stores/nodeStore';
import { readScript } from '@/types/scriptNode';
import { InputAsset } from '../../hooks/useInputAssets';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// 把 prompt 字符串中的 @[nodeId|title] 渲染成可视化标签 HTML
// v1.4.88：positionalSrcs 为参考图行图片 src 顺序表（与请求侧 inputImages 注入顺序一致），
// 锚定区块「@资产名」走名字芯片渲染（v1.4.98 前锚定区块为位置式「@图片N」，节点含
// 分镜帧等额外输入图时编号错位而弃用）；@图片N 仍支持用户手动输入的位置引用渲染
// v1.4.89：正文中「@名字」精确匹配名字表时渲染缩略图芯片（观感对齐既有候选芯片）；
// 两类芯片均把原文存入 data-pos-text / data-name-text，domToPrompt 按原文还原，编辑往返无损
// v1.4.91：名字表改由调用方传入（nameEntries，订阅自 store 全量节点的「脚本资产名优先+画布标题兜底」表）。
// 修复：原先在函数内从入参 nodeMap 构建，但入参 nodeMap 是收窄订阅 Map（仅含 @标记引用节点与候选节点），
// 分镜生图场景下脚本节点不在其中（参考图行候选 id 是 scriptId#assetId 伪 id，查不回脚本节点），
// 导致正文「@角色名」永远匹配不到图源、缩略图芯片不渲染
function promptToHtml(
  prompt: string,
  nodeMap: Map<string, AppNode>,
  positionalSrcs?: string[],
  nameEntries: { name: string; src: string }[] = [],
): string {
  if (!prompt) return '';
  // 长名优先排序（先到先得），避免「@短名」截断「@长名」的匹配
  const sortedNames = [...nameEntries].sort((a, b) => b.name.length - a.name.length);
  const nameAlt = sortedNames.map((e) => e.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  // 联合正则单次替换：@图片N（位置编号）优先，其次 @名字；避免链式 replace 误伤已生成的标签
  const plainRe = new RegExp(nameAlt ? `@图片(\\d+)|@(${nameAlt})` : '@图片(\\d+)', 'g');
  const parts = prompt.split(/(@\[[^\]]+\])/g);
  return parts
    .map((part) => {
      const match = part.match(/^@\[([^|]+)\|([^\]]*)\]$/);
      if (!match) {
        return escapeHtml(part)
          .replace(/\n/g, '<br>')
          // 「@图片N」纯文本芯片：独立类名 prompt-mention-pos（不进 prompt-mention 体系），
          // 显示文字与既有候选芯片一致（「图片 N」，无 @ 前缀）；原文存 data-pos-text 供往返还原
          .replace(plainRe, (all, posNum?: string, name?: string) => {
            if (posNum !== undefined) {
              const src = positionalSrcs?.[Number(posNum) - 1] || '';
              const img = src ? `<img src="${escapeHtml(src)}" class="w-4 h-4 rounded object-cover" alt="" />` : '';
              return `<span class="prompt-mention-pos inline-flex items-center gap-1 align-middle px-1.5 py-0.5 rounded-md bg-white/10 text-white/90 text-[13px] leading-none" contenteditable="false" data-pos-text="${escapeHtml(all)}" title="参考图 ${posNum}">${img}<span>图片 ${posNum}</span></span>`;
            }
            const entry = sortedNames.find((e) => e.name === name);
            if (!entry) return escapeHtml(all);
            const img = `<img src="${escapeHtml(entry.src)}" class="w-4 h-4 rounded object-cover" alt="" />`;
            return `<span class="prompt-mention-name inline-flex items-center gap-1 align-middle px-1.5 py-0.5 rounded-md bg-white/10 text-white/90 text-[13px] leading-none" contenteditable="false" data-name-text="${escapeHtml(all)}" title="${escapeHtml(entry.name)}">${img}<span>${escapeHtml(entry.name)}</span></span>`;
          });
      }
      const [, nodeId, title] = match;
      const n = nodeMap.get(nodeId);
      const src = n?.data.image || n?.data.images?.[0] || '';
      // v1.4.19：候选可为无图引用（脚本面板上游文本节点）——src 为空时不渲染 img，避免破图
      const img = src ? `<img src="${escapeHtml(src)}" class="w-4 h-4 rounded object-cover" alt="" />` : '';
      return `<span class="prompt-mention inline-flex items-center gap-1 align-middle px-1.5 py-0.5 rounded-md bg-white/10 text-white/90 text-[13px] leading-none" contenteditable="false" data-mention-id="${nodeId}">${img}<span>${escapeHtml(title || '图片')}</span></span>`;
    })
    .join('');
}

// 统一的 DOM 节点 → prompt 字符串转换
// 与 getPromptOffset 的偏移计算完全一致，避免长文本换行（<br>/<div>）导致偏移错位
function domToPrompt(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node as Text).data;
  }
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement;
    if (el.classList?.contains('prompt-mention')) {
      const id = el.getAttribute('data-mention-id') || '';
      const title = el.textContent?.trim() || '';
      return `@[${id}|${title}]`;
    }
    // v1.4.89 位置/名字芯片：按 data-* 存储的原文还原，保证编辑往返与存储文本严格一致
    const posText = el.getAttribute('data-pos-text');
    if (el.classList?.contains('prompt-mention-pos') && posText) {
      return posText;
    }
    const nameText = el.getAttribute('data-name-text');
    if (el.classList?.contains('prompt-mention-name') && nameText) {
      return nameText;
    }
    if (el.tagName === 'BR') {
      return '\n';
    }
    let text = '';
    for (const child of Array.from(el.childNodes)) {
      text += domToPrompt(child);
    }
    // 块级元素末尾追加换行（与 contenteditable 的渲染行为一致）
    if (el.tagName === 'DIV' || el.tagName === 'P') {
      text += '\n';
    }
    return text;
  }
  return '';
}

// 把 contenteditable 的 HTML 转回 prompt 字符串
// 注意：不要在此处 trim，否则字符串长度与 getPromptOffset 返回的 DOM 偏移不一致，
// 导致 findMentionStart 的 cursor 越界返回 null，@弹窗不触发。
// trim 交给 syncPromptFromHtml 在写入 store 时做。
function htmlToPrompt(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  // 遍历根 div 的子节点（不对根 div 本身加换行），与 getPromptOffset 一致
  let text = '';
  for (const child of Array.from(div.childNodes)) {
    text += domToPrompt(child);
  }
  return text.replace(/\n+/g, '\n');
}

// mention span 在 prompt 中占用的文本长度
function getMentionPromptLength(node: HTMLElement): number {
  const id = node.getAttribute('data-mention-id') || '';
  const title = node.textContent?.trim() || '';
  return `@[${id}|${title}]`.length;
}

// 获取节点的 prompt 字符串长度（与 domToPrompt 逻辑完全一致）
function getNodePromptLength(node: Node): number {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node as Text).data.length;
  }
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement;
    if (el.classList?.contains('prompt-mention')) {
      return getMentionPromptLength(el);
    }
    // v1.4.89 位置/名字芯片：按原文长度计（与 domToPrompt 输出一致），保证光标偏移不错位
    if (el.classList?.contains('prompt-mention-pos') || el.classList?.contains('prompt-mention-name')) {
      return (el.getAttribute('data-pos-text') || el.getAttribute('data-name-text') || el.textContent || '').length;
    }
    if (el.tagName === 'BR') {
      return 1;
    }
    let len = 0;
    for (const child of Array.from(el.childNodes)) {
      len += getNodePromptLength(child);
    }
    if (el.tagName === 'DIV' || el.tagName === 'P') {
      len += 1;
    }
    return len;
  }
  return 0;
}

// 获取 contenteditable 中选区末尾在 prompt 字符串中的偏移
function getPromptOffset(element: HTMLElement): number {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return 0;
  const range = selection.getRangeAt(0);
  if (!element.contains(range.commonAncestorContainer)) return 0;
  const endContainer = range.endContainer;
  const endOffset = range.endOffset;

  // 返回 >=0 表示找到 endContainer 并返回光标偏移；返回 -1 表示 endContainer 不在 node 子树中
  function walk(node: Node): number {
    if (node === endContainer) {
      // 文本节点：endOffset 是字符偏移，直接返回
      if (node.nodeType === Node.TEXT_NODE) {
        return endOffset;
      }
      // 元素节点：endOffset 是子节点索引，需累加前 endOffset 个子节点的长度
      let len = 0;
      const children = Array.from(node.childNodes);
      for (let i = 0; i < endOffset && i < children.length; i++) {
        len += getNodePromptLength(children[i]);
      }
      return len;
    }
    // 非 endContainer 的文本/mention 节点：不在子树中
    if (node.nodeType === Node.TEXT_NODE) {
      return -1;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      // v1.4.89：位置/名字芯片与 mention 同待遇——光标不会落入 contenteditable=false 的芯片内部
      if (el.classList?.contains('prompt-mention') || el.classList?.contains('prompt-mention-pos') || el.classList?.contains('prompt-mention-name')) {
        return -1;
      }
      let len = 0;
      let found = false;
      for (const child of Array.from(el.childNodes)) {
        const childResult = walk(child);
        if (childResult >= 0) {
          // 找到 endContainer，累加偏移后停止遍历后续子节点
          len += childResult;
          found = true;
          break;
        }
        // endContainer 不在此 child 中，累加 child 的完整 prompt 长度
        len += getNodePromptLength(child);
      }
      if (!found) {
        return -1; // endContainer 不在当前元素子树中
      }
      // 关键：endContainer 在当前 div/p 内部时，不加末尾换行符
      // （光标还在元素内部，没越过换行符）
      return len;
    }
    return -1;
  }

  const result = walk(element);
  return result >= 0 ? result : 0;
}

// 设置 contenteditable 中光标到指定的 prompt 偏移
function setPromptOffset(element: HTMLElement, offset: number) {
  const selection = window.getSelection();
  if (!selection) return;

  function walk(node: Node, current: number): { node: Node; offset: number; found: boolean } | null {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = (node as Text).length;
      if (current + len >= offset) {
        return { node, offset: Math.max(0, offset - current), found: true };
      }
      return { node, offset: len, found: false };
    }
    if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).classList?.contains('prompt-mention')) {
      const len = getMentionPromptLength(node as HTMLElement);
      if (current + len >= offset) {
        // 光标落在 mention 内部时，定位到 mention 元素后面
        return { node, offset: (node as HTMLElement).childNodes.length, found: true };
      }
      return { node, offset: (node as HTMLElement).childNodes.length, found: false };
    }
    let cur = current;
    for (const child of Array.from(node.childNodes)) {
      const res = walk(child, cur);
      if (!res) continue;
      if (res.found) return res;
      // 统一用 getNodePromptLength 累加偏移，正确处理 br/div/p 换行符
      cur += getNodePromptLength(child);
    }
    return null;
  }

  const res = walk(element, 0);
  if (!res) return;
  const range = document.createRange();
  try {
    range.setStart(res.node, Math.min(res.offset, res.node.nodeType === Node.TEXT_NODE ? (res.node as Text).length : (res.node as HTMLElement).childNodes.length));
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  } catch {
    // ignore
  }
}

// 从 prompt 字符串中查找未闭合的 @ 起始位置
function findMentionStart(prompt: string, cursor: number): { index: number; text: string } | null {
  let i = cursor - 1;
  while (i >= 0) {
    const ch = prompt[i];
    if (ch === '@') {
      // 如果 @ 后面是 [，说明是已经闭合的 @[...] 标记，不是正在输入的 @
      if (prompt[i + 1] === '[') {
        return null;
      }
      return { index: i, text: prompt.slice(i + 1, cursor) };
    }
    if (ch === ' ' || ch === '\n') return null;
    i--;
  }
  return null;
}

interface ModeTab {
  id: string;
  label: string;
}

const MODE_TABS: Record<NodeType, ModeTab[]> = {
  [NodeType.VIDEO_GENERATOR]: [
    { id: 'txt2video', label: '文生视频' },
    { id: 'omni', label: '全能参考' },
    { id: 'img2video', label: '图生视频' },
    { id: 'first_last', label: '首尾帧' },
    { id: 'img_ref', label: '图片参考' },
  ],
  [NodeType.IMAGE_GENERATOR]: [
    { id: 'txt2img', label: '文生图' },
    { id: 'img2img', label: '图生图' },
    { id: 'upscale', label: '高清重绘' },
    { id: 'style', label: '风格迁移' },
  ],
  [NodeType.AUDIO_GENERATOR]: [
    { id: 'tts', label: '文字转语音' },
    { id: 'audio2video', label: '音频生视频' },
  ],
  [NodeType.PROMPT_INPUT]: [
    { id: 'write', label: '自己编写' },
    { id: 'txt2video', label: '文生视频' },
    { id: 'img2prompt', label: '图片反推' },
    { id: 'txt2music', label: '文字生音乐' },
  ],
  [NodeType.IMAGE_EDITOR]: [{ id: 'edit', label: '图像编辑' }],
  [NodeType.MEDIA_UPLOAD]: [],
  [NodeType.TEXT_NODE]: [],
  [NodeType.SCRIPT_NODE]: [],
  [NodeType.VIDEO_COMPOSE]: [],
  [NodeType.PANORAMA_GENERATOR]: [
    { id: 'txt2panorama', label: '文生全景' },
    { id: 'img2panorama', label: '图生全景' },
  ],
  [NodeType.DIRECTOR]: [],
  [NodeType.DETAIL_PAGE]: [],
  [NodeType.LAYER_EDIT]: [],
  [NodeType.GROUP]: [],
};

const IMAGE_MODE_MAP: Record<string, ImageGenerationMode> = {
  'txt2img': 'TXT2IMG',
  'img2img': 'IMG2IMG',
  'upscale': 'UPSCALE',
  'style': 'STYLE',
};

const IMAGE_MODE_TO_TAB: Record<string, string> = {
  'TXT2IMG': 'txt2img',
  'IMG2IMG': 'img2img',
  'UPSCALE': 'upscale',
  'STYLE': 'style',
};

const placeholders: Record<NodeType, string> = {
  [NodeType.IMAGE_GENERATOR]: '描述你想要生成的画面内容，按/呼出指令，@引用素材',
  [NodeType.VIDEO_GENERATOR]: '描述你想要生成的视频内容...',
  [NodeType.AUDIO_GENERATOR]: '描述你想生成的音乐或音效...',
  [NodeType.PROMPT_INPUT]: '写下你想讲的故事、场景或角色设定。例如：一个来自未来的机器人，在城市屋顶看星星。',
  [NodeType.IMAGE_EDITOR]: '描述你的修改需求...',
  [NodeType.MEDIA_UPLOAD]: '',
  [NodeType.TEXT_NODE]: '',
  [NodeType.SCRIPT_NODE]: '描述剧情片段、故事，为你生成分镜脚本',
  [NodeType.VIDEO_COMPOSE]: '',
  [NodeType.PANORAMA_GENERATOR]: '描述你想要的全景画面，例如"生成一张科技展厅的720°全景图"，支持上传场景参考图。',
  [NodeType.DIRECTOR]: '',
  [NodeType.DETAIL_PAGE]: '',
  [NodeType.LAYER_EDIT]: '',
  [NodeType.GROUP]: '',
};

const imageModePlaceholders: Record<string, string> = {
  'TXT2IMG': '描述你想要生成的画面内容，按/呼出指令，@引用素材',
  'IMG2IMG': '描述你想要生成的画面，参考图将作为基础...',
  'UPSCALE': '描述增强需求，如"提升清晰度、补充细节"...',
  'STYLE': '描述想要的风格，参考图将作为内容基础...',
};

interface PromptEditorProps {
  nodeId: string;
  nodeType: NodeType;
  prompt: string;
  currentImageMode?: ImageGenerationMode;
  inputAssets: InputAsset[];
  isWorking: boolean;
  currentMode: string;
  onPromptChange: (value: string) => void;
  onPromptSubmit: () => void;
  onImageModeSwitch?: (mode: ImageGenerationMode) => void;
  onAddUploadNode?: () => void;
  onUpdate: (id: string, data: any) => void;
  onSetCurrentMode: (mode: string) => void;
  onSetIsInputFocused: (v: boolean) => void;
  onGenerate: () => void;
  /** v1.4.19 外部直接提供 @ 候选（提供时替代 inputAssets 图片候选推导；脚本面板上游文本节点等非图片引用场景） */
  mentionCandidates?: { id: string; title: string; src: string }[];
  /** v1.4.19 编辑区 relative 包裹层追加类名（展开面板 flex-1 伸展场景） */
  editorWrapClassName?: string;
  /** v1.4.19 编辑区内联样式覆盖（默认固定 80/120px 高度；展开面板传 flex 撑满） */
  editorStyle?: React.CSSProperties;
}

interface MentionCandidate {
  id: string;
  title: string;
  src: string;
  index: number;
}

const PromptEditorComponent: React.FC<PromptEditorProps> = ({
  nodeId,
  nodeType,
  prompt,
  currentImageMode,
  inputAssets,
  isWorking,
  currentMode,
  onPromptChange,
  onPromptSubmit,
  onImageModeSwitch,
  onAddUploadNode,
  onUpdate,
  onSetCurrentMode,
  onSetIsInputFocused,
  onGenerate,
  mentionCandidates,
  editorWrapClassName,
  editorStyle,
}) => {
  const isUpscaleMode = nodeType === NodeType.IMAGE_GENERATOR && currentImageMode === 'UPSCALE';
  const availableModes = MODE_TABS[nodeType] || [];
  const activeMode = nodeType === NodeType.IMAGE_GENERATOR
    ? (IMAGE_MODE_TO_TAB[currentImageMode || 'TXT2IMG'] || 'txt2img')
    : nodeType === NodeType.PANORAMA_GENERATOR
      ? (currentMode || (inputAssets.length > 0 ? 'img2panorama' : 'txt2panorama'))
      : (currentMode || (availableModes[0]?.id || ''));

  const editorRef = useRef<HTMLDivElement>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionPos, setMentionPos] = useState<{ left: number; top: number } | null>(null);
  const mentionCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isComposing = useRef(false);
  const isUpdatingHtml = useRef(false);

  // 收窄订阅：从 prompt 解析被引用的 nodeId + inputAssets 中的候选 nodeId，
  // 只订阅这些节点的 image src 映射。避免订阅整个 nodeMap 导致任何节点变更都触发重渲染。
  // selector 返回拼接字符串，只有被引用/候选节点的图片变化才触发更新。
  // 修复：加入 inputAssets 的 nodeId，确保首次插入 mention 时 nodeMap 已有候选图片数据
  const mentionImageSrcs = useNodeStore(
    useCallback((state) => {
      const ids = new Set<string>();
      // 已引用的 nodeId
      if (prompt) {
        const re = /@\[([^|]+)\|[^\]]*\]/g;
        let m;
        while ((m = re.exec(prompt)) !== null) {
          ids.add(m[1]);
        }
      }
      // 候选的 nodeId（确保首次插入时 nodeMap 有数据）：外部候选优先，否则回退 inputAssets 图片
      if (mentionCandidates) {
        for (const c of mentionCandidates) ids.add(c.id);
      } else {
        for (const asset of inputAssets) {
          if (asset.type === 'image') ids.add(asset.id);
        }
      }
      if (ids.size === 0) return '';
      const parts: string[] = [];
      for (const id of ids) {
        const n = state.nodeMap.get(id);
        const src = n?.data.image || n?.data.images?.[0] || '';
        parts.push(id + ':' + src);
      }
      return parts.join('|');
    }, [prompt, inputAssets, mentionCandidates]),
  );

  // 由拼接字符串还原为 Map，供 promptToHtml 使用
  const nodeMap = useMemo(() => {
    const map = new Map<string, AppNode>();
    if (!mentionImageSrcs) return map;
    const fullMap = useNodeStore.getState().nodeMap;
    for (const part of mentionImageSrcs.split('|')) {
      const [id] = part.split(':');
      const n = fullMap.get(id);
      if (n) map.set(id, n);
    }
    return map;
  }, [mentionImageSrcs]);

  // v1.4.91：名字芯片图源订阅——遍历 store 全量节点构建「名字→图片」签名表
  //（脚本资产名优先，画布节点标题兜底）。拼接为签名字符串：只有名字或图片变化才触发重渲染，
  // 拖拽/位置等变更不影响。不能复用上面的收窄 nodeMap：分镜生图场景下脚本节点与
  // 角色画布节点均不在收窄范围内，正文「@角色名」会永远匹配不到图源
  const nameTableSig = useNodeStore(
    useCallback((state) => {
      const parts: string[] = [];
      const seen = new Set<string>();
      // ① 脚本节点资产表：分镜链路「参考图」的语义源头，与请求注入图同源（资产图重生成自动跟随）
      for (const n of state.nodeMap.values()) {
        if (n.type !== NodeType.SCRIPT_NODE) continue;
        const script = readScript((n.data as { script?: unknown }).script);
        for (const asset of script?.assets || []) {
          const name = asset.name?.trim();
          if (!name || seen.has(name) || !asset.imageUrl) continue;
          seen.add(name);
          parts.push(name + '\u0000' + asset.imageUrl);
        }
      }
      // ② 画布节点标题 + 节点自身图：非脚本场景兜底
      for (const n of state.nodeMap.values()) {
        const name = n.title?.trim();
        if (!name || seen.has(name)) continue;
        const src = n.data.image || n.data.images?.[0] || '';
        if (src) {
          seen.add(name);
          parts.push(name + '\u0000' + src);
        }
      }
      return parts.join('\u0001');
    }, []),
  );

  // 由签名字符串还原名字表，供 promptToHtml 使用
  const nameEntries = useMemo(() => {
    if (!nameTableSig) return [];
    const entries: { name: string; src: string }[] = [];
    for (const part of nameTableSig.split('\u0001')) {
      const sep = part.indexOf('\u0000');
      if (sep <= 0) continue;
      const name = part.slice(0, sep);
      const src = part.slice(sep + 1);
      if (name && src) entries.push({ name, src });
    }
    return entries;
  }, [nameTableSig]);

  // 候选列表：当前面板已引用为参考的图片，按引用顺序编号为 图片1、图片2...
  // v1.4.19：外部 mentionCandidates 优先（脚本面板上游文本节点等非图片引用）
  const imageCandidates = useMemo(() => {
    if (mentionCandidates) {
      return mentionCandidates.map((c, i) => ({ ...c, index: i + 1 }));
    }
    const list: MentionCandidate[] = [];
    let index = 0;
    for (const asset of inputAssets) {
      if (asset.type !== 'image') continue;
      index += 1;
      list.push({
        id: asset.id,
        title: `图片 ${index}`,
        src: asset.src,
        index,
      });
    }
    return list;
  }, [inputAssets, mentionCandidates]);

  const filteredCandidates = useMemo(() => {
    const q = mentionQuery.trim().toLowerCase();
    if (!q) return imageCandidates;
    return imageCandidates.filter((c) => c.title.toLowerCase().includes(q));
  }, [imageCandidates, mentionQuery]);

  // v1.4.88：「@图片N」编号对应的缩略图源 = 参考图行图片顺序（含分镜锚定展开，与请求侧同序）
  const positionalImageSrcs = useMemo(
    () => inputAssets.filter((a) => a.type === 'image').map((a) => a.src),
    [inputAssets],
  );

  // prompt 变化时同步 HTML（外部更新/初始化）
  // v1.4.19：聚焦时跳过同步避免打断输入；外部清空（发送后重置草稿，编辑区仍聚焦）例外，须强制同步
  useEffect(() => {
    const el = editorRef.current;
    if (!el || (document.activeElement === el && prompt !== '')) return;
    isUpdatingHtml.current = true;
    const html = promptToHtml(prompt, nodeMap, positionalImageSrcs, nameEntries);
    if (el.innerHTML !== html) {
      el.innerHTML = html;
    }
    isUpdatingHtml.current = false;
  }, [prompt, nodeMap, positionalImageSrcs, nameEntries]);

  const syncPromptFromHtml = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    // trim 放在这里做，保证存入 store 的 prompt 无首尾空白
    // htmlToPrompt 本身不 trim，以保持与 getPromptOffset 的偏移一致
    const newPrompt = htmlToPrompt(el.innerHTML).trim();
    if (newPrompt !== prompt) {
      onPromptChange(newPrompt);
    }
  }, [onPromptChange, prompt]);

  const checkMention = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const currentPrompt = htmlToPrompt(el.innerHTML);
    const cursor = getPromptOffset(el);
    const found = findMentionStart(currentPrompt, cursor);
    if (found) {
      const selection = window.getSelection();
      const rects = selection?.getRangeAt(0)?.getClientRects();
      const rect = rects?.[0] || el.getBoundingClientRect();
      setMentionStart(found.index);
      setMentionQuery(found.text);
      setMentionIndex(0);
      setMentionPos({ left: rect.left, top: rect.top + rect.height });
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
      setMentionPos(null);
    }
  }, []);

  const insertMention = useCallback((candidate: MentionCandidate) => {
    const el = editorRef.current;
    if (!el) return;
    const currentPrompt = htmlToPrompt(el.innerHTML);
    const before = currentPrompt.slice(0, mentionStart);
    const after = currentPrompt.slice(mentionStart + 1 + mentionQuery.length);
    const marker = `@[${candidate.id}|${candidate.title}]`;
    // mention 后加一个空格，方便继续输入
    const newText = before + marker + ' ' + after;
    // 修复：使用完整的 nodeMap 而非可能过时的订阅 Map
    // 首次插入 mention 时，订阅 Map 可能为空（prompt 之前无 @[...] 标记），
    // 导致 promptToHtml 中 nodeMap.get(nodeId) 返回 undefined，img src 为空
    const fullNodeMap = useNodeStore.getState().nodeMap;
    const newHtml = promptToHtml(newText, fullNodeMap, positionalImageSrcs, nameEntries);
    el.innerHTML = newHtml;
    syncPromptFromHtml();
    setMentionOpen(false);
    setMentionQuery('');
    setMentionPos(null);
    // 光标移到 mention span 后面的空格后面
    requestAnimationFrame(() => {
      const selection = window.getSelection();
      if (!selection) return;
      const spans = el.querySelectorAll('.prompt-mention');
      let insertedSpan: Element | null = null;
      for (let i = spans.length - 1; i >= 0; i--) {
        if (spans[i].getAttribute('data-mention-id') === candidate.id) {
          insertedSpan = spans[i];
          break;
        }
      }
      const range = document.createRange();
      if (insertedSpan && insertedSpan.nextSibling && insertedSpan.nextSibling.nodeType === Node.TEXT_NODE) {
        const textNode = insertedSpan.nextSibling as Text;
        range.setStart(textNode, Math.min(1, textNode.length));
      } else if (insertedSpan) {
        const textNode = document.createTextNode(' ');
        insertedSpan.parentNode?.insertBefore(textNode, insertedSpan.nextSibling);
        range.setStart(textNode, 1);
      } else {
        range.selectNodeContents(el);
        range.collapse(false);
      }
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      el.focus();
    });
  // v1.4.88：函数体引用 positionalImageSrcs（@图片N 芯片缩略图源），须入 deps；
  // v1.4.91：函数体引用 nameEntries（@名字 芯片图源），须入 deps；
  // nodeMap 经 useNodeStore.getState() 现取全量，无需 nodeMap 依赖
  }, [mentionStart, mentionQuery, syncPromptFromHtml, positionalImageSrcs, nameEntries]);

  const handleModeTabClick = useCallback((modeTab: ModeTab) => {
    if (nodeType === NodeType.IMAGE_GENERATOR && onImageModeSwitch) {
      const targetImageMode = IMAGE_MODE_MAP[modeTab.id];
      if (targetImageMode) {
        onImageModeSwitch(targetImageMode);
      }
    } else if (nodeType === NodeType.PANORAMA_GENERATOR) {
      if (modeTab.id === 'img2panorama' && inputAssets.length === 0 && onAddUploadNode) {
        onAddUploadNode();
      }
      onSetCurrentMode(modeTab.id);
    } else {
      onSetCurrentMode(modeTab.id);
    }
  }, [nodeType, onImageModeSwitch, inputAssets.length, onAddUploadNode, onSetCurrentMode]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (mentionOpen && filteredCandidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % filteredCandidates.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + filteredCandidates.length) % filteredCandidates.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        insertMention(filteredCandidates[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onPromptSubmit();
      onGenerate();
    }
  }, [mentionOpen, filteredCandidates, mentionIndex, insertMention, onPromptSubmit, onGenerate]);

  const handlePromptBlur = useCallback(() => {
    onSetIsInputFocused(false);
    syncPromptFromHtml();
    onPromptSubmit();
    mentionCloseTimer.current = setTimeout(() => {
      setMentionOpen(false);
      setMentionPos(null);
    }, 150);
  }, [onPromptSubmit, onSetIsInputFocused, syncPromptFromHtml]);

  const getImagePlaceholder = () => {
    if (nodeType === NodeType.IMAGE_GENERATOR && currentImageMode) {
      return imageModePlaceholders[currentImageMode] || imageModePlaceholders['TXT2IMG'];
    }
    return placeholders[nodeType] || '描述您的生成需求...';
  };

  const isEmpty = !prompt;

  return (
    <>
      {availableModes.length > 0 && !isUpscaleMode && (
        <div className="flex items-center gap-2 flex-wrap">
          {availableModes.map((modeTab) => {
            const isActive = activeMode === modeTab.id;
            const isVideoNode = nodeType === NodeType.VIDEO_GENERATOR;
            const isPanoramaNode = nodeType === NodeType.PANORAMA_GENERATOR;
            const hasInput = inputAssets.length > 0;
            let isDisabled = false;
            if (nodeType === NodeType.IMAGE_GENERATOR && modeTab.id === 'txt2img' && hasInput) {
              isDisabled = true;
            } else if (isVideoNode) {
              if (!hasInput && modeTab.id !== 'txt2video') isDisabled = true;
              else if (hasInput && modeTab.id === 'txt2video') isDisabled = true;
            } else if (isPanoramaNode) {
              if (hasInput && modeTab.id === 'txt2panorama') isDisabled = true;
            }
            return (
              <button
                key={modeTab.id}
                onClick={() => !isDisabled && handleModeTabClick(modeTab)}
                className={`px-3 py-1 rounded-lg text-xs transition-all duration-200 ${
                  isDisabled
                    ? 'text-white/15 cursor-not-allowed'
                    : isActive
                      ? 'bg-white/10 text-white/90 font-medium'
                      : 'text-white/40 hover:text-white/60 hover:bg-white/5'
                }`}
              >
                {modeTab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* v1.4.19：条件收紧为「有可见内容才渲染」，SCRIPT_NODE 等无按钮/无资产类型不再输出空行（与原行为等价的并集改写） */}
      {((nodeType === NodeType.IMAGE_GENERATOR && !isUpscaleMode) || nodeType === NodeType.VIDEO_GENERATOR || nodeType === NodeType.PANORAMA_GENERATOR || inputAssets.length > 0) && (
      <div className="flex items-center gap-2">
        {nodeType === NodeType.IMAGE_GENERATOR && !isUpscaleMode && (
          <>
            <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="风格"><Palette size={14} /><span className="text-[9px] leading-none">风格</span></button>
            <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="标记"><MapPin size={14} /><span className="text-[9px] leading-none">标记</span></button>
            <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="聚焦"><Focus size={14} /><span className="text-[9px] leading-none">聚焦</span></button>
          </>
        )}
        {nodeType === NodeType.VIDEO_GENERATOR && (
          <>
            <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="标记"><MapPin size={14} /><span className="text-[9px] leading-none">标记</span></button>
            <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="运镜"><VideoIcon size={14} /><span className="text-[9px] leading-none">运镜</span></button>
            <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="角色库"><Users size={14} /><span className="text-[9px] leading-none">角色库</span></button>
          </>
        )}
        {nodeType === NodeType.PANORAMA_GENERATOR && (
          <>
            <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="风格"><Palette size={14} /><span className="text-[9px] leading-none">风格</span></button>
            <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="标记"><MapPin size={14} /><span className="text-[9px] leading-none">标记</span></button>
          </>
        )}
        {inputAssets.length > 0 && inputAssets.slice(0, 4).map((asset, index) => (
          <div key={asset.id} className="relative w-9 h-9 rounded-lg overflow-hidden border border-white/20 flex-shrink-0" style={asset.role === 'style-reference' ? { borderColor: '#6D5BF5' } : undefined}>
            {asset.type === 'video' ? (
              <video src={asset.src} className="w-full h-full object-cover" muted loop autoPlay />
            ) : (
              <img src={asset.src} className="w-full h-full object-cover" alt={`参考 ${index + 1}`} />
            )}
            {asset.role === 'style-reference' && (
              <div className="absolute top-0 left-0 h-3.5 px-1 rounded-br-md flex items-center justify-center" style={{ background: '#6D5BF5' }} title="竞品风格参考图（仅参考风格，不参考产品与品牌）">
                <span className="text-[8px] font-bold text-white leading-none">风格</span>
              </div>
            )}
            <div className="absolute bottom-0 right-0 min-w-4 h-4 px-1 bg-black/85 rounded-tl-md flex items-center justify-center">
              <span className="text-[10px] font-bold text-white leading-none">{index + 1}</span>
            </div>
          </div>
        ))}
      </div>
      )}

      {!isUpscaleMode && (
        <div className={`relative group/input ${editorWrapClassName || ''}`}>
          {isEmpty && (
            <div className="absolute left-0 top-0 pointer-events-none text-[13px] text-white/20 font-medium leading-relaxed select-none">
              {getImagePlaceholder()}
            </div>
          )}
          <div
            ref={editorRef}
            contentEditable={!isWorking}
            className={`w-full bg-transparent text-[13px] text-slate-200 p-0 focus:outline-none font-medium leading-relaxed overflow-y-auto panel-scrollbar empty:before:content-[attr(data-placeholder)] empty:before:text-white/20 empty:before:font-medium ${isWorking ? 'cursor-default' : 'cursor-text'}`}
            style={{ height: '80px', minHeight: '80px', maxHeight: '120px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...editorStyle }}
            data-placeholder={getImagePlaceholder()}
            onInput={() => {
              if (isUpdatingHtml.current) return;
              syncPromptFromHtml();
              checkMention();
            }}
            onCompositionStart={() => { isComposing.current = true; }}
            onCompositionEnd={() => {
              isComposing.current = false;
              syncPromptFromHtml();
              checkMention();
            }}
            onBlur={handlePromptBlur}
            onFocus={() => {
              onSetIsInputFocused(true);
              checkMention();
            }}
            onKeyDown={handleKeyDown}
            onClick={() => checkMention()}
            onMouseDown={(e) => e.stopPropagation()}
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData('text/plain');
              document.execCommand('insertText', false, text);
            }}
          />
          {mentionOpen && mentionPos && createPortal(
            <div
              className="fixed z-[9999] min-w-[160px] max-w-[280px] w-fit max-h-[260px] overflow-y-auto rounded-xl bg-[#1f1f1f]/95 backdrop-blur-xl border border-white/10 shadow-2xl p-1.5 flex flex-col gap-0.5"
              style={{ left: mentionPos.left, top: mentionPos.top }}
              onMouseDown={(e) => {
                e.preventDefault();
                if (mentionCloseTimer.current) {
                  clearTimeout(mentionCloseTimer.current);
                  mentionCloseTimer.current = null;
                }
              }}
            >
              {imageCandidates.length === 0 ? (
                <div className="px-2 py-1.5 text-[11px] text-white/40 whitespace-nowrap">暂无引用图片</div>
              ) : filteredCandidates.length === 0 ? (
                <div className="px-2 py-1.5 text-[11px] text-white/40 whitespace-nowrap">无匹配结果</div>
              ) : (
                filteredCandidates.map((candidate, idx) => (
                  <button
                    key={candidate.id}
                    type="button"
                    className={`flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-lg transition-colors ${
                      idx === mentionIndex ? 'bg-white/10' : 'hover:bg-white/5'
                    }`}
                    onClick={() => insertMention(candidate)}
                    onMouseEnter={() => setMentionIndex(idx)}
                  >
                    <div className="relative w-7 h-7 rounded-md overflow-hidden border border-white/10 flex-shrink-0 bg-white/5">
                      {candidate.src ? (
                        <img src={candidate.src} className="w-full h-full object-cover" alt="" />
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1.5 min-w-0 pr-1">
                      <span className="text-[12px] text-white/90 truncate">{candidate.title}</span>
                      <span className="text-[10px] text-white/40 whitespace-nowrap">(@{candidate.index})</span>
                    </div>
                  </button>
                ))
              )}
            </div>,
            document.body
          )}
        </div>
      )}
    </>
  );
};

// memo + 自定义比较：避免父组件 EditorPanel 重渲染时牵连
// 比较关键 props，忽略回调引用变化（回调由父级 useCallback 稳定）
export const PromptEditor = memo(PromptEditorComponent, (prev, next) => {
  return (
    prev.nodeId === next.nodeId &&
    prev.nodeType === next.nodeType &&
    prev.prompt === next.prompt &&
    prev.currentImageMode === next.currentImageMode &&
    prev.isWorking === next.isWorking &&
    prev.currentMode === next.currentMode &&
    // inputAssets 数组内容比较
    prev.inputAssets.length === next.inputAssets.length &&
    prev.inputAssets.every((a, i) => a.id === next.inputAssets[i].id && a.src === next.inputAssets[i].src && a.type === next.inputAssets[i].type) &&
    prev.editorWrapClassName === next.editorWrapClassName &&
    JSON.stringify(prev.editorStyle || null) === JSON.stringify(next.editorStyle || null) &&
    JSON.stringify(prev.mentionCandidates || null) === JSON.stringify(next.mentionCandidates || null)
  );
});
